import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import { HRApiWrapper } from 'api/HRApiWrapper';

interface ProfilesSectionProps {
  employeeId: number;
  employeeName?: string;
  employeeLastName?: string;
}

const ProfilesSection: React.FC<ProfilesSectionProps> = ({ employeeId, employeeName, employeeLastName }) => {
  const api: Api = useApi();
  const hrApi = new HRApiWrapper(api.employeeApi);
  const toast = useToastMessageQueue();
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProfiles = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await hrApi.getProfilesByEmployee('1', employeeId);
      setProfiles(res.data?.data ?? []);
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'role', headerName: intl.get('hr.profile.role'), flex: 1, valueGetter: (_: any, row: any) => row.role?.name ?? '' },
    { field: 'seniority', headerName: intl.get('hr.profile.seniority'), width: 150, valueGetter: (_: any, row: any) => row.seniority?.name ?? '' },
    {
      field: 'skillCount', headerName: intl.get('hr.profile.skills'), width: 110, sortable: false,
      renderCell: (params) => (
        <Chip label={(params.row.profileSkills ?? []).filter((s: any) => !s.isDeleted).length} size="small" />
      ),
    },
    {
      field: 'cvFileUri', headerName: intl.get('hr.profile.cv'), width: 90, sortable: false,
      renderCell: (params) => params.value
        ? <Chip label="CV" size="small" color="success" />
        : <Chip label={intl.get('hr.profile.noCV')} size="small" variant="outlined" />,
    },
    {
      field: 'view', headerName: '', width: 60, sortable: false,
      renderCell: () => (
        <IconButton
          size="small"
          color="primary"
          onClick={() => navigate('/hr/profiles', {
            state: { employeeId, employeeName, employeeLastName },
          })}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Typography color="primary" variant="h6" sx={{ mb: 2 }}>
        {intl.get('mainNavigation.humanResources.profiles') || 'Profiles'}
      </Typography>
      {loading ? <Spinner /> : (
        <Box style={{ width: '100%', display: 'flex' }}>
          <DataGrid
            rows={profiles}
            columns={columns}
            pageSizeOptions={[5, 10]}
            sortingMode="client"
            initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Box>
      )}
    </Box>
  );
};

export default ProfilesSection;
